import { LayoutList, LayoutGrid, BarChart3, SlidersHorizontal, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { ListViewLayoutMode } from '@/lib/views/types';

interface ListViewSwitcherProps {
  currentMode: ListViewLayoutMode;
  onModeChange: (mode: ListViewLayoutMode) => void;
  disabled?: boolean;
  enableSimpleView?: boolean;
  enableReportView?: boolean;
  enableAdvancedView?: boolean;
  /** "pill" = icon-only rounded-md pill (default), "tabs" = ClickUp-style text tabs with underline */
  variant?: 'pill' | 'tabs';
  /** Labels for tab variant */
  labels?: {
    simple?: string;
    table?: string;
    gallery?: string;
    report?: string;
    advanced?: string;
  };
}

export function ListViewSwitcher({
  currentMode,
  onModeChange,
  disabled = false,
  enableSimpleView = false,
  enableReportView = false,
  enableAdvancedView = false,
  variant = 'pill',
  labels,
}: ListViewSwitcherProps) {
  if (variant === 'tabs') {
    return (
      <div className="flex items-center gap-0.5 -ml-2.5">
        {enableSimpleView && (
          <TabButton
            active={currentMode === 'simple'}
            disabled={disabled}
            onClick={() => onModeChange('simple')}
            icon={<span className="flex items-center justify-center h-5 w-5 rounded-xs bg-sky-500 shrink-0"><Sparkles className="h-3 w-3 text-white" /></span>}
            label={labels?.simple ?? 'Simple'}
          />
        )}
        <TabButton
          active={currentMode === 'table'}
          disabled={disabled}
          onClick={() => onModeChange('table')}
          icon={<span className="flex items-center justify-center h-5 w-5 rounded-xs bg-violet-500 shrink-0"><LayoutList className="h-3 w-3 text-white" /></span>}
          label={labels?.table ?? 'List'}
        />
        <TabButton
          active={currentMode === 'gallery'}
          disabled={disabled}
          onClick={() => onModeChange('gallery')}
          icon={<span className="flex items-center justify-center h-5 w-5 rounded-xs bg-emerald-500 shrink-0"><LayoutGrid className="h-3 w-3 text-white" /></span>}
          label={labels?.gallery ?? 'Board'}
        />
        {enableReportView && (
          <TabButton
            active={currentMode === 'report'}
            disabled={disabled}
            onClick={() => onModeChange('report')}
            icon={<span className="flex items-center justify-center h-5 w-5 rounded-xs bg-amber-500 shrink-0"><BarChart3 className="h-3 w-3 text-white" /></span>}
            label={labels?.report ?? 'Table'}
          />
        )}
        {enableAdvancedView && (
          <TabButton
            active={currentMode === 'advanced'}
            disabled={disabled}
            onClick={() => onModeChange('advanced')}
            icon={<span className="flex items-center justify-center h-5 w-5 rounded-xs bg-sky-500 shrink-0"><SlidersHorizontal className="h-3 w-3 text-white" /></span>}
            label={labels?.advanced ?? 'Advanced'}
            className="hidden md:flex"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center rounded-full border border-border bg-white dark:bg-gray-900 p-1">
      {enableSimpleView && (
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "h-8 w-8 p-0 rounded-full transition-colors",
            currentMode === 'simple' && "bg-accent"
          )}
          onClick={() => onModeChange('simple')}
          aria-label="Simple view"
          aria-pressed={currentMode === 'simple'}
        >
          <Sparkles className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        className={cn(
          "h-8 w-8 p-0 rounded-full transition-colors",
          currentMode === 'table' && "bg-accent"
        )}
        onClick={() => onModeChange('table')}
        aria-label="Table view"
        aria-pressed={currentMode === 'table'}
      >
        <LayoutList className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        className={cn(
          "h-8 w-8 p-0 rounded-full transition-colors",
          currentMode === 'gallery' && "bg-accent"
        )}
        onClick={() => onModeChange('gallery')}
        aria-label="Gallery view"
        aria-pressed={currentMode === 'gallery'}
      >
        <LayoutGrid className="h-4 w-4" />
      </Button>
      {enableReportView && (
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "h-8 w-8 p-0 rounded-full transition-colors",
            currentMode === 'report' && "bg-accent"
          )}
          onClick={() => onModeChange('report')}
          aria-label="Report view"
          aria-pressed={currentMode === 'report'}
        >
          <BarChart3 className="h-4 w-4" />
        </Button>
      )}
      {enableAdvancedView && (
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "hidden md:flex h-8 w-8 p-0 rounded-full transition-colors",
            currentMode === 'advanced' && "bg-accent"
          )}
          onClick={() => onModeChange('advanced')}
          aria-label="Advanced table view"
          aria-pressed={currentMode === 'advanced'}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function TabButton({
  active,
  disabled,
  onClick,
  icon,
  label,
  className,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-2.5 py-1.5 text-sm transition-colors whitespace-nowrap rounded-xs",
        active
          ? "text-foreground font-medium bg-neutral-100 dark:bg-neutral-800"
          : "text-muted-foreground font-normal hover:text-foreground hover:bg-neutral-50 dark:hover:bg-neutral-800/50",
        disabled && "opacity-50 pointer-events-none",
        className,
      )}
    >
      {icon}
      {label}
    </button>
  );
}
